import { createContext, use, useEffect, useRef, type ReactNode } from 'react';
import { useModal } from '@customerio/pluma-components/react';
import type { PlumaManagedModalId } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { VideoLibraryModal } from './video-library-modal';

interface VideoLibraryContextValue {
  openVideoLibrary: (videoId?: string) => void;
  closeVideoLibrary: () => void;
}

const VideoLibraryContext = createContext<VideoLibraryContextValue | null>(null);

interface VideoLibraryProviderProps {
  children: ReactNode;
}

export function VideoLibraryProvider({ children }: VideoLibraryProviderProps) {
  const { track } = useAnalytics();
  const videoLibraryModal = useModal(VideoLibraryModal);
  // The id of the currently open modal, so repeated `open` calls (e.g. from the
  // Ember window bridge) don't stack a second instance.
  const openModalIdRef = useRef<PlumaManagedModalId<typeof VideoLibraryModal> | null>(null);
  // Bumped on every `openVideoLibrary` call so the modal resets its active
  // video even when the requested video id matches the previous request.
  const openRequestIdRef = useRef(0);

  // `track`'s identity changes on workspace switch (see the bridge comment
  // below). Push it into an already-open modal so its analytics don't stick
  // to whichever workspace was active when it last opened/updated.
  useEffect(() => {
    if (openModalIdRef.current != null) {
      videoLibraryModal.update(openModalIdRef.current, { track });
    }
  }, [track, videoLibraryModal]);

  const openVideoLibrary = (videoId?: string) => {
    openRequestIdRef.current += 1;
    const data = {
      initialVideoId: videoId ?? null,
      openRequestId: openRequestIdRef.current,
      track,
    };
    if (openModalIdRef.current != null) {
      // Already open — switch the open modal to the requested video instead of
      // stacking a second instance.
      videoLibraryModal.update(openModalIdRef.current, data);
    } else {
      // `withImmediateCallbacks` clears the guard as soon as closing starts, so
      // the library can be reopened without waiting for the close animation.
      // `shouldCloseOnUnmount` closes the modal if VideoLibraryProvider unmounts
      // while it's open (e.g. a logout redirect to /login), so it doesn't
      // linger over the wrong shell.
      const modalId = videoLibraryModal.open(data, { withImmediateCallbacks: true, shouldCloseOnUnmount: true });
      openModalIdRef.current = modalId;
      void videoLibraryModal.waitForClose(modalId).then(() => {
        openModalIdRef.current = null;
      });
    }
    void track('customer_io_basics', 'modal_viewed', {});
  };

  const closeVideoLibrary = () => {
    if (openModalIdRef.current != null) {
      videoLibraryModal.close(openModalIdRef.current);
    }
  };

  // The bridge below is assigned once on mount, so route its calls through
  // refs updated each render. Freezing the first render's closures would pin a
  // stale `track` (its identity changes on workspace switch, and it no-ops
  // before a workspace is loaded).
  const openVideoLibraryRef = useRef(openVideoLibrary);
  openVideoLibraryRef.current = openVideoLibrary;
  const closeVideoLibraryRef = useRef(closeVideoLibrary);
  closeVideoLibraryRef.current = closeVideoLibrary;

  // Expose the video library functions to the window object
  // so that the ember ui can hook into it and open the video library
  useMountEffect(() => {
    Object.assign(window, {
      __hydra_video_library__: {
        open: (videoId?: string) => openVideoLibraryRef.current(videoId),
        close: () => closeVideoLibraryRef.current(),
      } satisfies Window['__hydra_video_library__'],
    });

    return () => {
      delete window.__hydra_video_library__;
    };
  });

  return (
    <VideoLibraryContext.Provider value={{ openVideoLibrary, closeVideoLibrary }}>
      {children}
    </VideoLibraryContext.Provider>
  );
}

export function useVideoLibrary() {
  const context = use(VideoLibraryContext);

  if (context == null) {
    throw new Error('useVideoLibrary must be used within VideoLibraryProvider');
  }

  return context;
}
